import { AppointmentStatus, AppointmentType } from "~/api/API";

// ── Appointment Type Sets ──────────────────────────────────────────

/**
 * Re-evaluation appointment types.
 */
export const REEVALUATION_TYPES = new Set<AppointmentType>([
  AppointmentType.QME_REEVALUATION,
  AppointmentType.AME_REEVALUATION,
  AppointmentType.SIBTF_REEVALUATION,
  AppointmentType.IME_REEVALUATION,
  AppointmentType.IME_ADR_REEVALUATION,
]);

/**
 * Types whose on-site op is NOT derived from where the visit happens.
 *
 * A supplemental is report work: it carries its parent's location but nobody
 * is on site for it, so the doctor+location mapping names the wrong person.
 * The backend routes these from the physician's supplemental report-ops
 * config after the clone commits (`services/onsite_ops`), so the client must
 * not re-derive them from `OnsiteOpsConfig` and overwrite that.
 *
 * Mirror of `LOCATION_INDEPENDENT_TYPES` in `services/onsite_ops/models.py`.
 */
export const LOCATION_INDEPENDENT_ONSITE_OP_TYPES = new Set<AppointmentType>([
  AppointmentType.SUPPLEMENTAL,
]);

/**
 * All evaluation appointment types (first evals + re-evals).
 */
export const EVALUATION_TYPES = new Set<AppointmentType>([
  AppointmentType.QME,
  AppointmentType.AME,
  AppointmentType.SIBTF,
  AppointmentType.IME,
  AppointmentType.IME_ADR,
  ...REEVALUATION_TYPES,
]);

/**
 * Appointment types that are non-in-person and should be marked N/A
 * for certain checklist items (interpreter, history, hoi, parties, n110, notice).
 */
export const NON_IN_PERSON_TYPES = new Set<AppointmentType>([
  AppointmentType.DEPOSITION,
  AppointmentType.SUPPLEMENTAL,
]);

// ── Appointment Status Sets ────────────────────────────────────────

/**
 * Statuses treated as "inquiry" (appointment not yet scheduled).
 */
export const INQUIRY_STATUSES = new Set<AppointmentStatus>([
  AppointmentStatus.INQUIRY,
  AppointmentStatus.INQUIRY_CLOSED,
]);

/**
 * Statuses served by the inquiry-style editing page (/appointments/new/:id)
 * instead of the read-only detail page. REEVAL_HOLD joins the inquiry family
 * here: a re-eval that isn't on the calendar yet is edited like an inquiry
 * until "Create appointment" converts it to SCHEDULED.
 */
export const INQUIRY_PAGE_STATUSES = new Set<AppointmentStatus>([
  ...INQUIRY_STATUSES,
  AppointmentStatus.REEVAL_HOLD,
]);

/**
 * Null-tolerant predicate for {@link INQUIRY_PAGE_STATUSES} — use it at the
 * routing branches that pick the inquiry page over the detail page.
 */
export const isInquiryPageStatus = (
  status: AppointmentStatus | null | undefined,
): boolean => !!status && INQUIRY_PAGE_STATUSES.has(status);

/**
 * Statuses treated as "cancelled" (appointment was cancelled).
 */
export const CANCELLED_STATUSES = new Set<AppointmentStatus>([
  AppointmentStatus.CANCELLED,
  AppointmentStatus.LATE_CANCELLED,
]);

/**
 * Statuses treated as "rescheduled" (appointment was rescheduled or in rescheduling flow).
 */
export const RESCHEDULED_STATUSES = new Set<AppointmentStatus>([
  AppointmentStatus.RESCHEDULED,
  AppointmentStatus.LATE_RESCHEDULED,
  AppointmentStatus.RESCHEDULING_HOLD,
  AppointmentStatus.LATE_RESCHEDULING_HOLD,
  AppointmentStatus.RESCHEDULING_IN_PROGRESS,
  AppointmentStatus.LATE_RESCHEDULING_IN_PROGRESS,
]);

/**
 * Appointment statuses where the appointment did not actually occur.
 * Used to exclude appointments from date-based calculations.
 */
export const NON_OCCURRED_STATUSES = new Set<AppointmentStatus>([
  AppointmentStatus.NO_SHOW,
  ...CANCELLED_STATUSES,
  ...RESCHEDULED_STATUSES,
  AppointmentStatus.DEPOSITION_HOLD,
  AppointmentStatus.REEVAL_HOLD,
]);

/**
 * Appointment statuses that are excluded from chain-aware UI surfaces
 * (related-appointments filter, Medical documents sidebar buckets). These
 * are appointments that didn't / won't happen as scheduled.
 */
export const CHAIN_EXCLUDED_STATUSES = new Set<AppointmentStatus>([
  AppointmentStatus.RESCHEDULED,
  AppointmentStatus.LATE_RESCHEDULED,
  AppointmentStatus.LATE_CANCELLED,
  AppointmentStatus.CANCELLED,
  AppointmentStatus.RESCHEDULING_HOLD,
  AppointmentStatus.LATE_RESCHEDULING_HOLD,
  AppointmentStatus.DEPOSITION_HOLD,
  AppointmentStatus.REEVAL_HOLD,
]);
